import axios from 'axios';
import { count } from '../monitor';
import { DEFAULT_AB_TESTS } from './constants';

interface PegasusTranslateResponse {
    originalTerm: any;
    translatedTerm: TranslateHash;
    sourceLocale: string;
    status: string;
}
export interface TranslateHash {
    [key: string]: string;
}

export interface SourceInfo {
    name: string;
    component?: { name?: string };
    gig?: { id?: number };
    user?: any;
    userGuid?: string;
    page?: {
        ctxId?: string;
        name?: string;
    };
    localization?: {
        experienceLocale?: string;
        browserLanguage?: string;
    };
    contextId?: string;
    context?: string;
}

export interface ServerTranslateProps {
    pegasusUrl: string;
    text: string | string[] | unknown;
    targetLocale: string;
    sourceLocale?: string;
    useServerCache?: boolean;
    writeCache?: boolean;
    sourceInfo: SourceInfo;
    context?: any;
    knownCrawler?: boolean;
    timeout?: number;
    abTests?: Record<string, unknown>;
}
interface PegasusProps {
    text: string | string[] | unknown;
    targetLocale: string;
    sourceLocale?: string;
    useServerCache: boolean;
    writeCache: boolean;
    abTests: Record<string, unknown>;
    timeout?: number;
    context: string | Record<string, string>;
    knownCrawler: any;
    sourceInfo: SourceInfo;
}
export const serverTranslate = async ({
    pegasusUrl,
    text,
    targetLocale,
    sourceLocale,
    useServerCache = false,
    writeCache = true,
    sourceInfo,
    context,
    knownCrawler = false,
    timeout,
    abTests = DEFAULT_AB_TESTS,
}: ServerTranslateProps) => {
    const params = convertPropsToPegasusProps({
        text,
        targetLocale,
        sourceLocale,
        useServerCache,
        writeCache,
        abTests,
        timeout,
        context,
        knownCrawler,
        sourceInfo,
    });

    let response: PegasusTranslateResponse;
    try {
        response = await axios.post(pegasusUrl, params).then(({ data, status }) => ({ status, ...data }));

        monitorStatus({
            source: sourceInfo?.name,
            status: response?.status,
        });
    } catch (e) {
        monitorStatus({
            source: sourceInfo?.name,
            status: 'ERROR',
        });

        throw e;
    }

    return formatResponse(response);
};

export const formatLocale = (locale: string | undefined) => {
    if (!locale || locale === 'auto') {
        return locale;
    }
    return locale.substr(0, 2).toLowerCase();
};

function monitorStatus({ source, status }: { source: string; status: string }): void {
    count(`source.${source}.status.${status}.variant.mtservice`);
}

const convertPropsToPegasusProps = (props: PegasusProps) => ({
    term: props.text,
    targetLanguage: formatLocale(props.targetLocale),
    sourceLanguage: formatLocale(props.sourceLocale),
    readCache: props.useServerCache,
    writeCache: props.writeCache,
    abTests: props.abTests,
    timeout: props.timeout,
    context: props.context || props.sourceInfo.context,
    knownCrawler: props.knownCrawler,
    sourceInfo: props.sourceInfo,
});

const formatResponse = (response: PegasusTranslateResponse) => ({
    originalBlob: response.originalTerm,
    translatedBlob: response.translatedTerm,
    sourceLocale: response.sourceLocale,
    status: convertStatusNameToHttpCode(response.status),
});

export const convertStatusNameToHttpCode = (status: string): number => {
    const statusesMap = {
        Success: 200,
        Failure: 500,
        Partial: 207,
    };

    return statusesMap[status] || status;
};
